import { SchemaVersionError } from './errors';
import { log } from './logger';
import { openDb } from '../data/db';

/**
 * **B-21 — the boot question §13.5's screen is the answer to:** *is the data on this device newer
 * than this copy of the app?*
 *
 * One function, one question, in `app/` because §1.2 says so: *"`ui/` … may import `app/`,
 * `domain/`, `config/`, `types/`. **Never** `data/` directly"*. The shell has to know which of two
 * worlds it is booting into, and `openDb()` is a `data/` call, so this is the seam between them.
 *
 * ── Why it is a gate and not a `try` around the first read ───────────────────
 *
 * Before this unit nothing called `openDb()` at all — storage was reached through `db()`, which lets
 * Dexie auto-open on the first table operation. The newer-schema condition therefore surfaced as
 * whichever read happened to run first, which on B is `resolveResume()`, and the user was shown
 * *"Touchline could not read where you left off"*: true, unhelpful, and describing a symptom two
 * layers away from the cause. Asking the question deliberately, once, before a shell is chosen, is
 * what lets the answer be a screen instead of an error message.
 *
 * ── Everything that is NOT this condition is deliberately passed through ────
 *
 * A quota failure, a corrupt file, a browser refusing IndexedDB in private mode — none of those is
 * *"the app is out of date"*, and none of them is fixed by an export-and-update screen. Sending them
 * here would put a confident, specific and **wrong** explanation in front of the user. They are
 * logged (NFR-8: reported, never swallowed) and left to the path that already handles them, which
 * ends at the shell's existing failure arm.
 */

const MODULE = 'schema-gate';

export type SchemaStatus =
  /** The stored schema is one this bundle understands. Normal boot. */
  | 'usable'
  /** §13.5: the stored schema is newer than this bundle. The recovery screen, and nothing else. */
  | 'newer-than-app';

/**
 * Opens the database and reports which world this is.
 *
 * **Never rejects.** A shell that had to catch this to decide what to render would be one `try` away
 * from rendering nothing at all, and a blank screen at a venue is the outcome every part of this
 * design is arranged to avoid.
 */
export async function checkSchema(): Promise<SchemaStatus> {
  try {
    await openDb();
    return 'usable';
  } catch (cause) {
    if (cause instanceof SchemaVersionError) {
      log.warn(MODULE, '§13.5: the stored schema is newer than this bundle; routing to recovery', {
        context: cause.context,
      });
      return 'newer-than-app';
    }
    log.error(MODULE, 'the database could not be opened, for a reason that is not a version gap', {
      message: cause instanceof Error ? cause.message : String(cause),
    });
    return 'usable';
  }
}
